import { Component, Input, Output, EventEmitter } from '@angular/core';
import * as Highcharts from 'highcharts';
import { catchError, forkJoin, map, Observable, of, Subscription, tap } from 'rxjs';
import { timeseriesChartXaxis, reportChartLineOptions } from '../highcharts-options';
import {
  PositionStatus,
  QuickBuild,
  QuickBuildService,
  UIFilter,
  UIFilterGroup,
  UniqueIdGenerator,
} from 'src/app/people/quick-build.service';
import { PeopleReportService, ReportResponse } from 'src/app/people/report-service.service';
import * as moment from 'moment';
import { getPeopleColumnByName } from 'src/app/people/people-columns';
import {
  BaseReportComponent,
  ReportResponseWithName,
  ChartContextMenuItem,
} from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { SearchFilter } from 'ldt-people-api';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import * as dayjs from 'dayjs';
import { HeadcountComparisonConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const REPORT_NAME = 'demographics';

@Component({
  selector: 'app-reports-headcount-comparison',
  templateUrl: './headcount-comparison.component.html',
  styleUrls: ['./headcount-comparison.component.scss'],
  standalone: false,
})
export class HeadcountComparisonComponent extends BaseReportComponent<HeadcountComparisonConfig> {
  @Input() quickBuilds: QuickBuild[];
  @Input() dateFrom: string;
  @Input() dateTo: string;
  @Input() boxShadow: boolean = true;

  protected readonly CHART_ID = 'headcount-comparison';
  protected readonly CHART_DATA_DESC = 'Headcount over time by company';

  chartId: string;
  subscriptions: Subscription = new Subscription();

  @Input()
  set selectedHeadcountChartValueType(value: 'percentage' | 'number' | undefined) {
    if (value) {
      this._selectedHeadcountChartValueType = value;
    }
  }
  get selectedHeadcountChartValueType(): 'percentage' | 'number' {
    return this._selectedHeadcountChartValueType;
  }

  @Input()
  set selectedHeadcountChartType(value: 'column' | 'histogram' | undefined) {
    if (value) {
      this._selectedHeadcountChartType = value;
    }
  }
  get selectedHeadcountChartType(): 'column' | 'histogram' {
    return this._selectedHeadcountChartType;
  }

  private _selectedHeadcountChartValueType: 'percentage' | 'number' = 'percentage';
  private _selectedHeadcountChartType: 'column' | 'histogram' = 'histogram';

  chartTypeOptions = [
    { value: 'histogram', displayText: 'By Month' },
    { value: 'column', displayText: 'Total' },
  ];

  valueTypeOptions = [
    { value: 'percentage', displayText: '%' },
    { value: 'number', displayText: '#' },
  ];

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );
    this.chartId = UniqueIdGenerator.generate();
    if (!this.dateFrom || !this.dateTo) {
      [this.dateFrom, this.dateTo] = this.getDefaultDateRange();
    }

    // Register configuration properties for automatic emission
    this.registerConfigProperty(
      'selectedHeadcountChartValueType',
      () => this._selectedHeadcountChartValueType
    );
    this.registerConfigProperty(
      'selectedHeadcountChartType',
      () => this._selectedHeadcountChartType
    );
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportChartLineOptions)
    );
    this.chart.update(demographicsChartNoSeries, true, true);
    Log.d('HeadcountComparisonComponent initializeChart', this.chart);
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    Log.d('headcount-comparison getChartData', this.quickBuilds, this.dateFrom, this.dateTo);
    if (!this.quickBuilds) return;
    // Set this primarily for the chart subtitles to generate in base-report
    this.quickBuild = this.quickBuilds[0];

    this.showLoading();

    // We reference the chart directly when generating series, so make sure it's ready
    this.findChartContainer().then(() => {
      this.quickBuilds
        .filter((quickBuild) => quickBuild)
        .forEach((quickBuild) => {
          this.addQuickbuildToChart(quickBuild);
        });

      this.subscriptions.add(
        forkJoin(
          this.quickBuilds
            .filter((quickBuild) => quickBuild)
            .map((quickBuild) => {
              return this.addQuickbuildToChart(quickBuild);
            })
        ).subscribe({
          next: (data: (ReportResponseWithName | null)[]) => {
            this.chartLoadingComplete();
            Log.d('headcount-comparison getChartData next');
          },
          error: (error) => {
            Log.e('headcount-comparison getChartData error', error);
            this.showChartError(error);
          },
        })
      );
    });
  }

  addQuickbuildToChart(quickBuild: QuickBuild): Observable<ReportResponseWithName | null> {
    Log.d('headcount-comparison addQuickbuildToChart', quickBuild, this.dateFrom, this.dateTo);
    this.removeQuickbuildFromChart(quickBuild.id, quickBuild.name);
    const reportParams = {
      date_from: this.dateFrom,
      date_to: this.dateTo,
    };
    const sub = this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        REPORT_NAME,
        reportParams,
        this.reportTarget,
        quickBuild
      )
      .pipe(
        tap((data) => {
          this.generateChartSeries(quickBuild.id, quickBuild.name, data.data.demographics);
        }),
        map((data) => ({
          name: quickBuild.name,
          data: data,
        })),
        catchError((error) => {
          Log.e('HeadcountComparisonComponent addQuickbuildToChart error', error);
          throw error;
        })
      );

    return sub;
  }

  onBreakdownByChanged(value: string) {
    if (!value) return;

    Log.d('HeadcountComparisonComponent onBreakdownByChanged', value);
    this.resetChartData();
    this.getChartData();
  }

  async resetChartData() {
    switch (this._selectedHeadcountChartValueType) {
      case 'percentage':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Pct Change' },
          },
        });
        break;
      case 'number':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Number of Employees' },
          },
        });
        break;
    }

    switch (this.selectedHeadcountChartType) {
      case 'column':
        await this.updateChart([], undefined, undefined, {
          xAxis: {
            type: 'category',
          },
        });
        break;
      case 'histogram':
        await this.updateChart([], undefined, undefined, { xAxis: timeseriesChartXaxis });
        break;
    }

    await super.resetChartData();
  }

  generateChartSeries(quickBuildId: string, quickBuildName: string, inputData: any): void {
    let data = inputData.map((d: any) => [new Date(d.date).valueOf(), d.count_employees]);

    if (this._selectedHeadcountChartValueType === 'percentage') {
      const startValue = data[0][1];
      data.forEach((d: any) => {
        d[1] = ((d[1] - startValue) / startValue) * 100;
      });
    }

    if (this._selectedHeadcountChartType === 'column') {
      const lastPoint = data[data.length - 1] || [];

      // If they want a number, we have to subtract the first point from the last to get the diff
      let chartPoint = lastPoint[1] || 0;
      if (this._selectedHeadcountChartValueType === 'number') {
        const firstPoint = data[0] || [];
        chartPoint = chartPoint - (firstPoint[1] || 0);
      }
      this.addPointToSingleSeries({
        y: chartPoint,
        color: chartPoint >= 0 ? '#8e91ff' : '#f97316',
        name: quickBuildName,
        custom: {
          id: quickBuildId,
        },
      });
      this.chart.update({
        xAxis: {
          categories: this.chart.xAxis[0].categories.concat(quickBuildName),
        },
      });

      return;
    }

    this.addSeriesToChart({
      custom: { id: quickBuildId },
      id: quickBuildId,
      name: quickBuildName,
      data: data,
      type: 'spline',
      colorIndex: this.quickBuilds.findIndex((qb) => qb.id === quickBuildId),
    });
  }

  addPointToSingleSeries(point: Highcharts.PointOptionsType) {
    // We only have 1 series - get or init it
    const seriesName = 'Growth';
    let seriesToChange = this.chart?.series.find((s) => s.name === seriesName);
    if (!seriesToChange) {
      seriesToChange = this.chart.addSeries({
        name: seriesName,
        type: 'column',
        data: [],
        custom: { id: seriesName, showGroupIcon: false },
        colorIndex: 0,
        showInLegend: false,
      });
      this.chart.update({
        xAxis: {
          type: 'category',
          categories: [],
        },
      });
    }
    seriesToChange.addPoint(point);
  }

  removeQuickbuildFromChart(quickBuildId: string, quickBuildName: string) {
    if (!this.chart) return;
    this.chart.series.forEach((series) => {
      if (series.options.custom?.id === quickBuildId) {
        series.remove();
      }
    });
    // For column charts, we need to remove the data point, not the series
    if (this._selectedHeadcountChartType === 'column' && this.chart.series.length > 0) {
      const dataIndex = this.chart.series[0].data.findIndex(
        (d) => (d as any).custom.id === quickBuildId
      );
      if (dataIndex >= 0) {
        this.chart.series[0].data[dataIndex].remove();
      }
    }
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const date = dayjs(point.x as number);
    const value = point.y as number;
    const series = point.series;
    const category = point.category as string;
    const quickBuildId = series.options.custom?.id;
    const quickBuild = this.quickBuilds.find((qb) => qb.id === quickBuildId);
    if (!quickBuild) {
      Log.s('HeadcountComparisonComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
        seriesCustomOptions: series.options.custom ? JSON.stringify(series.options.custom) : '',
        quickBuilds: JSON.stringify(this.quickBuilds),
      });
      return [];
    }

    Log.d('HeadcountComparisonComponent getContextMenuItems', point);

    const qbCopy = QuickBuildService.copyQuickBuild(quickBuild);

    const startDateFilter: UIFilter = {
      filter: {
        field: 'jobs.started_at',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        date_to: date.toISOString(),
        date_from: date.toISOString(),
      },
    };
    const endDateFilter: UIFilter = {
      filter: {
        field: 'jobs.ended_at',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
        date_to: date.toISOString(),
      },
    };
    const activeAtFilter: UIFilter = {
      filter: {
        field: 'job_active_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
      },
    };

    Log.d('job-starts-and-ends-component getContextMenuItems', startDateFilter);

    return [
      {
        label: `View People that worked at ${series.name} in ${date.utc().format('MMM YYYY')}`,
        icon: 'calendar_month',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [activeAtFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          qbCopy.name = `People that worked at ${series.name} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      },
      {
        label: `View People that arrived at ${series.name} in ${date.utc().format('MMM YYYY')}`,
        icon: 'calendar_month',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [startDateFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.positionStatus = PositionStatus.First;
          qbCopy.name = `People that arrived at ${series.name} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      },
      {
        label: `View People that departed ${series.name} in ${date.utc().format('MMM YYYY')}`,
        icon: 'calendar_month',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [endDateFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.positionStatus = PositionStatus.Last;
          qbCopy.name = `People that departed ${series.name} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  onValueTypeChanged(value: string) {
    this._selectedHeadcountChartValueType = value as 'percentage' | 'number';
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  onChartTypeChanged(value: string) {
    this._selectedHeadcountChartType = value as 'column' | 'histogram';
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }
}

const demographicsChartNoSeries: Highcharts.Options = {
  title: {
    text: 'Headcount Growth',
  },
  xAxis: timeseriesChartXaxis,
  yAxis: [
    {
      title: {
        text: 'Pct Change',
      },
    },
  ],
};
